import { useNavigate } from 'react-router-dom';
import { Text, Button, Icon, InfoItem, IconButton } from '@intellecteu/common-ui';
import { getNextRunDate, useRoles } from 'app/utils';
import { Labels } from 'app/constants';
import {
  DomainDto,
  ParticipantDto,
  ValidatorDto,
  ScheduledBackupDto,
  ScheduledValidatorBackupDto,
  ScheduledValidatorPruneExtendedDto,
  ScheduledValidatorIdDumpDto,
} from 'data';

import { ui } from 'ui/styles';

type ScheduleData =
  | ScheduledBackupDto
  | ScheduledValidatorBackupDto
  | ScheduledValidatorPruneExtendedDto
  | ScheduledValidatorIdDumpDto
  | undefined;

interface ManagementDetailsProps {
  title: string;
  schedulePath: string;
  scheduleData?: ScheduleData;
  onRemove: () => void;
  isRemoving: boolean;
  phase?: DomainDto.phase | ParticipantDto.phase | ValidatorDto.phase;
  showHistoryButton?: boolean;
  historyPath?: string;
  historyButtonText?: string;
  isScheduleDisabled?: (scheduleData: ScheduleData, isNodeRunning: boolean) => boolean;
  shouldShowAddIcon?: (scheduleData: ScheduleData, isNodeRunning: boolean) => boolean;
  isDisabled?: boolean;
}

export const ManagementDetails = ({
  title,
  schedulePath,
  scheduleData,
  onRemove,
  isRemoving,
  phase,
  showHistoryButton = false,
  historyPath,
  historyButtonText = 'Show history',
  isScheduleDisabled,
  shouldShowAddIcon,
  isDisabled = false,
}: ManagementDetailsProps) => {
  const { isAdmin } = useRoles();
  const navigate = useNavigate();
  const nextRunDate = getNextRunDate(scheduleData);
  const isNodeRunning = phase === DomainDto.phase.RUNNING;

  const goToSchedulePage = () => navigate(schedulePath);
  const goToHistoryPage = () => historyPath && navigate(historyPath);

  // Default disable logic: disabled when node is not running or when explicitly disabled
  const defaultIsScheduleDisabled = !isNodeRunning || isDisabled;
  const scheduleDisabled = isScheduleDisabled
    ? isScheduleDisabled(scheduleData, isNodeRunning) || isDisabled
    : defaultIsScheduleDisabled;

  // Default add icon logic: show when node is running and not disabled
  const defaultShouldShowAddIcon = isNodeRunning && !isDisabled;
  const showAddIcon = shouldShowAddIcon
    ? shouldShowAddIcon(scheduleData, isNodeRunning) && !isDisabled
    : defaultShouldShowAddIcon;

  return (
    <div className={isDisabled ? ui.disabled : undefined}>
      <div className={ui.detailsHeaderText}>
        <Text weight="bold" size={Text.size.s}>
          {title}
        </Text>
        {isAdmin && (
          <Button
            variant="tertiary"
            size="xs"
            onClick={goToSchedulePage}
            disabled={scheduleDisabled}
            style={{ marginTop: '2px' }}
          >
            {showAddIcon && <Icon icon={Icon.icons.add} size="m" color="darkGrey" />}
            Schedule
          </Button>
        )}
      </div>

      {nextRunDate && (
        <div className={ui.detailsList}>
          <section className={ui.nextRunInfo}>
            <div className={ui.nextRunRow}>
              <InfoItem label={Labels.NEXT_RUN} value={nextRunDate || '–'} />
              {isAdmin && (
                <>
                  <IconButton
                    iconName={Icon.icons.edit}
                    iconSize="s"
                    onClick={goToSchedulePage}
                    disabled={isRemoving || isDisabled}
                  />
                  <IconButton
                    iconName={Icon.icons.remove}
                    iconSize="s"
                    onClick={onRemove}
                    disabled={isRemoving || isDisabled}
                  />
                </>
              )}
            </div>
          </section>
        </div>
      )}

      {showHistoryButton && historyPath && (
        <>
          <br />
          <Button variant="tertiary" size="xs" onClick={goToHistoryPage} disabled={isDisabled}>
            <span>{historyButtonText}</span>
            {isNodeRunning && !isDisabled && <Icon icon={Icon.icons.arrowForward} />}
          </Button>
        </>
      )}
    </div>
  );
};
